import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, Copy, Check } from 'lucide-react';
import { affiliateDashboardAPI } from '../../utils/api';
import SaudiRiyalIcon from './SaudiRiyalIcon';
import AffiliateCurrencyAmount from './AffiliateCurrencyAmount';

const AffiliateReferralLinkPage = () => {
  const { t, i18n } = useTranslation();
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);
  const FIXED_COMMISSION_AMOUNT = 300;

  useEffect(() => {
    const loadReferralData = async () => {
      try {
        setIsLoading(true);
        const data = await affiliateDashboardAPI.getReferralCode();
        setReferralCode(data.referralCode || '');
        setReferralLink(data.referralLink || '');
      } finally {
        setIsLoading(false);
      }
    };
    loadReferralData();
  }, []);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('affiliateDashboard.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className={`${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
        <h1 className="text-3xl font-bold text-foreground">{t('affiliateDashboard.yourReferralLink')}</h1>
        <p className="mt-2 text-muted-foreground">{t('affiliateDashboard.referralLinkSubtitle')}</p>
      </div>

      <div className="bg-card border border-border p-6 rounded-lg shadow">
        <div className={`space-y-5 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div>
            <label className={`block text-sm font-medium text-muted-foreground mb-2 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
              {t('affiliateDashboard.referralCode')}
            </label>
            <div className={`flex items-center gap-2 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
              <input
                type="text"
                value={referralCode}
                readOnly
                className={`flex-1 glass-input font-mono ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                dir="ltr"
              />
              <button
                onClick={() => copyToClipboard(referralCode, 'code')}
                className={`glass-button px-4 py-2 flex items-center gap-2 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}
              >
                {copiedField === 'code' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copiedField === 'code' ? t('common.copied') : t('common.copy')}
              </button>
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium text-muted-foreground mb-2 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
              {t('affiliateDashboard.referralLink')}
            </label>
            <div className={`flex items-start gap-2 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
              <input
                type="text"
                value={referralLink}
                readOnly
                className={`flex-1 glass-input font-mono text-sm ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                dir="ltr"
              />
              <button
                onClick={() => copyToClipboard(referralLink, 'link')}
                className={`glass-button px-4 py-2 flex items-center gap-2 shrink-0 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}
              >
                {copiedField === 'link' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copiedField === 'link' ? t('common.copied') : t('common.copy')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border p-6 rounded-lg shadow">
        <div className={`flex items-center ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <div className="p-2 bg-yellow-500/10 rounded-lg">
            <SaudiRiyalIcon />
          </div>
          <div className={i18n.language === 'ar' ? 'mr-4 text-right' : 'ml-4 text-left'}>
            <p className="text-sm font-medium text-muted-foreground">{t('affiliateDashboard.commission')}</p>
            <p className="text-2xl font-bold text-card-foreground">
              <AffiliateCurrencyAmount amount={FIXED_COMMISSION_AMOUNT} />
            </p>
            <p className="text-sm text-muted-foreground mt-1">{t('affiliateDashboard.commissionClarification')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateReferralLinkPage;

